import type {
  CandidateQueryDetail,
  CandidateQueryProgress,
  CandidateQueryState,
  CandidateRow,
  CandidateStatus,
  CountryProgress,
  LatestCandidateQuery,
  OutreachBulkPreviewResponse,
  OutreachBulkReserveRequest,
  OutreachBulkSendRequest,
  OutreachOutlierGroupCode,
  OutreachOutlierRowsResponse,
  OutreachOutlierSummary,
  OutreachSelectionResponse,
  SaveOutreachSelectionRequest,
} from '@aegislab/shared';
import { apiClient } from '@/shared/api';

/**
 * 후보 조건 조회와 선택 — 퀘스트 `7ca8908c`.
 *
 * 크롤러 조회는 **서버 간 Bearer** 라 브라우저가 직접 못 부른다. AXIOM 이 앞에 서서 권한과
 * 캠페인 스코프를 걸고 통과시키므로, 프론트는 이 경로만 안다.
 *
 * 서버 계약은 shared 와 백엔드가 든다 — 프론트에 사본을 세우지 않는다(apiContractCopyGuard).
 * 크롤러 응답 모양은 shared 에 없으므로(백엔드 전용 계약) 화면이 읽는 만큼만 여기서 좁게 적는다.
 */

const base = (scenarioId: string) => `/campaigns/${scenarioId}/candidate-queries`;

export const createCandidateQuery = async (
  scenarioId: string,
  body: { region: string; minFollowers: number; maxFollowers?: number }
): Promise<{ queryId: string; state: CandidateQueryState }> => {
  const response = await apiClient.post<{
    queryId: string;
    state: CandidateQueryState;
  }>(base(scenarioId), body);
  return response.data;
};

export const fetchLatestCandidateQuery = async (
  scenarioId: string,
  region: string
): Promise<LatestCandidateQuery> => {
  const response = await apiClient.get<LatestCandidateQuery>(`${base(scenarioId)}/latest`, {
    params: { region },
  });
  return response.data;
};

export const fetchCandidateQuery = async (
  scenarioId: string,
  queryId: string
): Promise<CandidateQueryDetail> => {
  const response = await apiClient.get<CandidateQueryDetail>(`${base(scenarioId)}/${queryId}`);
  return response.data;
};

export const fetchCandidatePage = async (
  scenarioId: string,
  queryId: string,
  page: { cursor?: string; limit?: number }
): Promise<{ items: CandidateRow[]; nextCursor: string | null }> => {
  const response = await apiClient.get<{
    items: CandidateRow[];
    nextCursor: string | null;
  }>(`${base(scenarioId)}/${queryId}/page`, { params: page });
  return response.data;
};

/** "조건에 맞는 대상 전체 선택" 이 청크로 받는다 — 화면이 반복 호출로 상한을 우회하지 않는다. */
export const fetchCandidateIds = async (
  scenarioId: string,
  queryId: string,
  page: { after?: string; limit?: number }
): Promise<{ ids: string[]; next: string | null }> => {
  const response = await apiClient.get<{ ids: string[]; next: string | null }>(
    `${base(scenarioId)}/${queryId}/ids`,
    { params: page }
  );
  return response.data;
};

/** `failed` 만, 최대 3회. 그 밖은 서버가 409 로 막는다. */
export const retryCandidateQuery = async (
  scenarioId: string,
  queryId: string
): Promise<{ queryId: string; state: CandidateQueryState }> => {
  const response = await apiClient.post<{
    queryId: string;
    state: CandidateQueryState;
  }>(`${base(scenarioId)}/${queryId}/retry`);
  return response.data;
};

/** 「찾는 크리에이터가 없나요? 직접 추가」 — 핸들 또는 프로필 링크 **하나**. */
export const addManualCandidate = async (
  scenarioId: string,
  input: string
): Promise<{
  handle: string;
  externalCreatorId: string | null;
  status: 'RESOLVED' | 'PENDING';
  requestId: number | null;
}> => {
  const response = await apiClient.post<{
    handle: string;
    externalCreatorId: string | null;
    status: 'RESOLVED' | 'PENDING';
    requestId: number | null;
  }>(`${base(scenarioId)}/manual`, { input });
  return response.data;
};

const selectionBase = (scenarioId: string, country: string) =>
  `/campaigns/${scenarioId}/outreach-draft/${country}/selection`;

/** 한 번도 안 고른 나라는 404 가 아니라 빈 선택으로 온다. */
export const fetchOutreachSelection = async (
  scenarioId: string,
  country: string
): Promise<OutreachSelectionResponse> => {
  const response = await apiClient.get<OutreachSelectionResponse>(
    selectionBase(scenarioId, country)
  );
  return response.data;
};

export const saveOutreachSelection = async (
  scenarioId: string,
  country: string,
  body: SaveOutreachSelectionRequest
): Promise<OutreachSelectionResponse> => {
  const response = await apiClient.put<OutreachSelectionResponse>(
    selectionBase(scenarioId, country),
    body
  );
  return response.data;
};

/**
 * 후보 행의 최근 접촉·기본 제외.
 *
 * 조회인데 POST 인 것은 id 가 최대 5,000개라 주소 줄에 안 들어가기 때문이다.
 */
export const fetchCandidateStatuses = async (
  scenarioId: string,
  externalCreatorIds: string[]
): Promise<{ statuses: CandidateStatus[] }> => {
  const response = await apiClient.post<{ statuses: CandidateStatus[] }>(
    `/campaigns/${scenarioId}/candidate-statuses`,
    { externalCreatorIds }
  );
  return response.data;
};

/** 나라별 진행 줄 — 캠페인 나라를 **전부** 준다. 안 보낸 나라는 `roundCount: 0` 이다. */
export const fetchOutreachProgress = async (
  scenarioId: string
): Promise<{ countries: CountryProgress[] }> => {
  const response = await apiClient.get<{ countries: CountryProgress[] }>(
    `/campaigns/${scenarioId}/outreach-progress`
  );
  return response.data;
};

/** 아무것도 보내지 않는다. 발송과 같은 명단을 읽으므로 여기 적힌 수가 곧 나갈 수다. */
export const previewOutreachBulk = async (
  scenarioId: string,
  body: { draftId: string; perCreatorSendIds?: string[]; reincludeIds?: string[] }
): Promise<OutreachBulkPreviewResponse> => {
  const response = await apiClient.post<OutreachBulkPreviewResponse>(
    `/campaigns/${scenarioId}/outreach-rounds/bulk/preview`,
    body
  );
  return response.data;
};

/** 「먼저 볼 것」 — 초안 명단을 다섯 묶음과 문제 없는 인원으로 가른다. 아무것도 바꾸지 않는다. */
export const fetchOutlierSummary = async (
  scenarioId: string,
  draftId: string,
  previewLimit?: number
): Promise<OutreachOutlierSummary> => {
  const response = await apiClient.post<OutreachOutlierSummary>(
    `/campaigns/${scenarioId}/candidate-outliers`,
    { draftId, previewLimit }
  );
  return response.data;
};

/** 묶음 하나의 행 페이지(최대 200행). 페이지마다 서버가 명단 전체를 다시 가른다. */
export const fetchOutlierRows = async (
  scenarioId: string,
  body: { draftId: string; code: OutreachOutlierGroupCode; offset?: number; limit?: number }
): Promise<OutreachOutlierRowsResponse> => {
  const response = await apiClient.post<OutreachOutlierRowsResponse>(
    `/campaigns/${scenarioId}/candidate-outliers/rows`,
    body
  );
  return response.data;
};

/**
 * 되돌릴 수 없는 발송 — 초안 명단을 1,000명씩 회차로 쪼개 보낸다. 같은 초안으로 다시 누르면
 * 서버가 멱등으로 같은 결과를 돌려준다. 화면은 응답을 읽지 않고 이력·진행 줄을 다시 부른다.
 */
export const sendOutreachBulk = async (
  scenarioId: string,
  body: OutreachBulkSendRequest
): Promise<void> => {
  await apiClient.post(`/campaigns/${scenarioId}/outreach-rounds/bulk`, body);
};

/**
 * 예약 — 회차와 받는 사람만 서버에 남고 스티비 작업은 백그라운드다(96445435). 201 은 예약 접수이지
 * 스티비 예약 확인이 아니다 — 확인은 발송 이력의 상태로 본다.
 */
export const reserveOutreachBulk = async (
  scenarioId: string,
  body: OutreachBulkReserveRequest
): Promise<void> => {
  await apiClient.post(`/campaigns/${scenarioId}/outreach-rounds/bulk/reserve`, body);
};

/** 예약 취소 — 서버가 스티비에서 취소를 확인한 뒤에만 취소로 적는다. */
export const cancelOutreachReservation = async (
  scenarioId: string,
  roundId: string
): Promise<void> => {
  await apiClient.post(`/campaigns/${scenarioId}/outreach-rounds/${roundId}/cancel-reservation`);
};

/**
 * 화면이 쓰는 타입은 **shared 가 정본**이다. 여기서는 그대로 다시 내보내기만 한다 — 프론트가
 * 자기 사본을 세우면 서버와 필드가 갈려도 아무것도 발화하지 않는다(`apiContractCopyGuard`).
 */
export type {
  CandidateQueryDetail,
  CandidateQueryProgress,
  CandidateQueryState,
  CandidateRow,
  CandidateStatus,
  CountryProgress,
  LatestCandidateQuery,
  OutreachBulkPreviewResponse,
  OutreachBulkSendRequest,
  OutreachOutlierGroupCode,
  OutreachOutlierRowsResponse,
  OutreachOutlierSummary,
};

export interface OtherChannelCandidate {
  externalCreatorId: string;
  handle: string;
  followers: number | null;
}

/**
 * 이메일 주소가 없어 다른 매체로 섭외할 사람 — 인원 지정과 같은 조회·순서·제외 규칙이다.
 * 틱톡 핸들까지만 알고 인스타그램·라인 주소는 이 조회에 없다.
 */
export const fetchOtherChannelCandidates = async (
  scenarioId: string,
  queryId: string,
  page: { cursor?: string; limit?: number }
): Promise<{ items: OtherChannelCandidate[]; nextCursor: string | null }> => {
  const response = await apiClient.get<{
    items: OtherChannelCandidate[];
    nextCursor: string | null;
  }>(`${base(scenarioId)}/${queryId}/other-channel`, { params: page });
  return response.data;
};
